import { LanguageService } from '$core/services/language.service';
import { environment } from '$env/environment';
import { Location } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateLoader,
  TranslateService,
} from '@ngx-translate/core';
import { LocalizeRouterSettings, ManualParserLoader } from 'localize-router';
import { of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { httpRetry } from './http-retry';

export interface Translation {
  json: any;
  revision: any;
}

class CustomTranslateLoader implements TranslateLoader {
  constructor(private http: HttpClient) {}

  public getTranslation(lang: string) {
    const currentRevision = this.getTranslationRevision(lang);

    let params = new HttpParams().set('language', lang);

    if (currentRevision) {
      params = params.set('revision', currentRevision);
    }

    return this.http.get<Translation>('/v1/translations', { params }).pipe(
      catchError((err) => {
        if (err.status === 503) {
          return of({});
        }
        return throwError(err);
      }),
      httpRetry(),
      map((res) => {
        if (!currentRevision) {
          this.setCachedTranslation(lang, res.json, res.revision);
          return res.json;
        }

        if (res.revision === currentRevision) {
          return this.getCachedTranslation(lang);
        } else {
          const mergedTranslates = {
            ...this.getCachedTranslation(lang),
            ...res.json,
          };

          this.setCachedTranslation(lang, mergedTranslates, res.revision);

          return mergedTranslates;
        }
      })
    );
  }

  public getCachedTranslation(lang: string): Translation {
    return JSON.parse(localStorage.getItem(lang));
  }

  public setCachedTranslation(
    lang: string,
    value: Translation,
    revision: number
  ) {
    if (revision || revision === 0) {
      localStorage.setItem(`${lang}_revision`, revision.toString());
    }

    localStorage.setItem(lang, JSON.stringify(value));
  }

  public getTranslationRevision(lang: string) {
    return localStorage.getItem(`${lang}_revision`);
  }
}

export function httpLoaderFactory(http: HttpClient) {
  return new CustomTranslateLoader(http);
}

export class CustomMissingTranslationHandler
  implements MissingTranslationHandler
{
  public handle(params: MissingTranslationHandlerParams) {
    return params.key;
  }
}

export function localizeLoaderFactory(
  translate: TranslateService,
  location: Location,
  settings: LocalizeRouterSettings
) {
  return new ManualParserLoader(
    translate,
    location,
    settings,
    environment.langs || [],
    ''
  );
}

export function defaultLangFunction() {
  return environment.langs[0];
}

export function dynamicLocaleFactory(languageService: LanguageService) {
  return languageService.currentLang;
}
